/**
 * components/Maps/RiderFleetMap.js
 * ─────────────────────────────────────────────────────────────────────────────
 * THE rider fleet map. One implementation, rendered by both tracking screens
 * (vendor in-house riders and 3PL partner riders), which previously carried
 * near-identical Leaflet code.
 *
 * WHY GOOGLE AND NOT LEAFLET
 *   CARTO began watermarking anonymous raster tiles ("API KEY REQUIRED"), and
 *   says those basemaps are being retired. The dashboard already loads the Maps
 *   JS API for address autocomplete, so Google was the rail with no new vendor,
 *   no new bill and a licence that covers commercial use.
 *
 * WHAT THIS OWNS
 *   The map instance, rider markers (with heading rotation and selection
 *   state), the trail polylines, one shared info window, first-load bounds
 *   fitting, panning to the selected rider, and the Uganda restriction.
 *
 * WHAT IT DOES NOT OWN
 *   Fetching, sockets, the rider side panel, counts. Each screen keeps those.
 *   This component is presentational: it draws the riders it is handed.
 *
 * REQUIREMENTS
 *   • GoogleMapsProvider must be mounted (it is, at src/index.js).
 *   • REACT_APP_GOOGLE_MAPS_MAP_ID must be set at BUILD time. Advanced markers
 *     refuse to render without a Map ID, so the map says so plainly rather than
 *     rendering an empty basemap and looking broken.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from "../../GoogleMapsContext";
import { buildRiderMarkerContent } from "./riderMarkerContent";

// Kampala, and the box beyond which the map will not pan.
const DEFAULT_CENTER = { lat: 0.3476, lng: 32.5825 };
const UGANDA_BOUNDS = {
  north: 4.3,
  south: -1.5,
  west: 29.5,
  east: 35.1,
};

const TRAIL_BUSY = "#f59e0b";
const TRAIL_FREE = "#10b981";

/**
 * @param {object}   props
 * @param {object[]} props.riders          riders that HAVE a location already
 * @param {function} props.getPosition     rider => {lat,lng} | null
 * @param {object}   props.trails          { [riderId]: [[lat,lng], …] }
 * @param {string?}  props.selectedRiderId
 * @param {function} props.onSelectRider   rider => void
 * @param {function} props.renderPopup     rider => ReactNode, shown in the info window
 * @param {string?}  props.partnerLogo     3PL logo pin; omit for plain rider pins
 * @param {number}   props.height
 */
const RiderFleetMap = ({
  riders = [],
  getPosition,
  trails = {},
  selectedRiderId = null,
  onSelectRider,
  renderPopup,
  partnerLogo = null,
  height = 520,
}) => {
  const { mapsApiLoaded } = useGoogleMaps() || {};
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map()); // riderId -> AdvancedMarkerElement
  const polylinesRef = useRef(new Map()); // riderId -> Polyline
  const infoWindowRef = useRef(null);
  const popupHostRef = useRef(null); // detached node React renders the popup into
  const hasFittedRef = useRef(false);
  const [openRiderId, setOpenRiderId] = useState(null);

  const riderById = useMemo(() => {
    const m = new Map();
    riders.forEach((r) => m.set(r._id, r));
    return m;
  }, [riders]);

  // ── Create the map once ───────────────────────────────────────────────────
  useEffect(() => {
    if (!mapsApiLoaded || !GOOGLE_MAPS_MAP_ID || mapRef.current) return;
    if (!containerRef.current || !window.google?.maps) return;

    mapRef.current = new window.google.maps.Map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 12,
      minZoom: 7,
      mapId: GOOGLE_MAPS_MAP_ID,
      restriction: { latLngBounds: UGANDA_BOUNDS, strictBounds: false },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      clickableIcons: false,
    });

    popupHostRef.current = document.createElement("div");
    infoWindowRef.current = new window.google.maps.InfoWindow({
      content: popupHostRef.current,
    });
    infoWindowRef.current.addListener("closeclick", () =>
      setOpenRiderId(null),
    );
  }, [mapsApiLoaded]);

  // ── Tear down on unmount ──────────────────────────────────────────────────
  useEffect(
    () => () => {
      markersRef.current.forEach((m) => {
        m.map = null;
      });
      markersRef.current.clear();
      polylinesRef.current.forEach((p) => p.setMap(null));
      polylinesRef.current.clear();
      if (popupHostRef.current) {
        ReactDOM.unmountComponentAtNode(popupHostRef.current);
      }
      infoWindowRef.current?.close();
      mapRef.current = null;
    },
    [],
  );

  // ── Markers: create, move, restyle, remove ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    const g = window.google;
    if (!map || !g?.maps?.marker?.AdvancedMarkerElement) return;

    const seen = new Set();

    riders.forEach((rider) => {
      const pos = getPosition(rider);
      if (!pos) return;
      seen.add(rider._id);

      const content = buildRiderMarkerContent({
        isOnline: rider.onlineStatus === "Online",
        hasActiveOrder: (rider.currentActiveOrders || 0) > 0,
        heading: rider.currentLocation?.heading ?? null,
        partnerLogo,
        isSelected: rider._id === selectedRiderId,
      });

      const existing = markersRef.current.get(rider._id);
      if (existing) {
        // Reassigning position lets the CSS transition animate the move.
        existing.position = pos;
        existing.content = content;
        existing.zIndex = rider._id === selectedRiderId ? 999 : 1;
        return;
      }

      const marker = new g.maps.marker.AdvancedMarkerElement({
        map,
        position: pos,
        content,
        title: `${rider.firstName || ""} ${rider.lastName || ""}`.trim(),
        zIndex: rider._id === selectedRiderId ? 999 : 1,
      });
      marker.addListener("click", () => {
        setOpenRiderId(rider._id);
        if (onSelectRider) onSelectRider(rider);
      });
      markersRef.current.set(rider._id, marker);
    });

    // Riders that went away.
    markersRef.current.forEach((marker, id) => {
      if (seen.has(id)) return;
      marker.map = null;
      markersRef.current.delete(id);
    });
  }, [riders, getPosition, partnerLogo, selectedRiderId, onSelectRider]);

  // ── Trails ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    const g = window.google;
    if (!map || !g?.maps) return;

    const seen = new Set();

    riders.forEach((rider) => {
      const trail = trails[rider._id] || [];
      if (trail.length < 2 || rider.onlineStatus !== "Online") return;
      seen.add(rider._id);

      const path = trail.map(([lat, lng]) => ({ lat, lng }));
      const color =
        (rider.currentActiveOrders || 0) > 0 ? TRAIL_BUSY : TRAIL_FREE;
      const existing = polylinesRef.current.get(rider._id);

      if (existing) {
        existing.setPath(path);
        existing.setOptions({ strokeColor: color });
        return;
      }

      polylinesRef.current.set(
        rider._id,
        new g.maps.Polyline({
          map,
          path,
          strokeColor: color,
          strokeOpacity: 0, // opacity lives on the dashes below
          strokeWeight: 3,
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 0.55,
                strokeWeight: 3,
                scale: 3,
              },
              offset: "0",
              repeat: "10px",
            },
          ],
        }),
      );
    });

    polylinesRef.current.forEach((line, id) => {
      if (seen.has(id)) return;
      line.setMap(null);
      polylinesRef.current.delete(id);
    });
  }, [riders, trails]);

  // ── Fit to the fleet, once, on first load ─────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    const g = window.google;
    if (!map || !g?.maps || hasFittedRef.current) return;

    const points = riders.map(getPosition).filter(Boolean);
    if (!points.length) return;

    const bounds = new g.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 60);

    // fitBounds on a single rider zooms to the max; pull it back to something
    // a dispatcher can read.
    const once = g.maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > 17) map.setZoom(17);
    });
    hasFittedRef.current = true;
    return () => g.maps.event.removeListener(once);
  }, [riders, getPosition]);

  // ── Pan to whoever is selected in the side panel ──────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedRiderId) return;
    const rider = riderById.get(selectedRiderId);
    const pos = rider && getPosition(rider);
    if (!pos) return;
    map.panTo(pos);
    if (map.getZoom() < 16) map.setZoom(16);
  }, [selectedRiderId, riderById, getPosition]);

  // ── Info window follows the open rider, and keeps its content live ────────
  useEffect(() => {
    const map = mapRef.current;
    const infoWindow = infoWindowRef.current;
    const host = popupHostRef.current;
    if (!map || !infoWindow || !host) return;

    if (!openRiderId) {
      infoWindow.close();
      ReactDOM.unmountComponentAtNode(host);
      return;
    }

    const rider = riderById.get(openRiderId);
    const marker = markersRef.current.get(openRiderId);
    if (!rider || !marker) {
      infoWindow.close();
      return;
    }

    // Re-rendering on every rider change keeps speed and last ping current
    // while the window is open.
    ReactDOM.render(renderPopup ? renderPopup(rider) : null, host);
    infoWindow.open({ map, anchor: marker });
  }, [openRiderId, riderById, renderPopup]);

  if (!GOOGLE_MAPS_MAP_ID) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 8,
          padding: 32,
          textAlign: "center",
          background: "#f8fafc",
          color: "#6b7280",
          fontSize: "0.84rem",
        }}
      >
        <div style={{ fontWeight: 700, color: "#1a2636" }}>
          Map unavailable
        </div>
        <div>
          This build has no Google Maps Map ID, so rider pins cannot be drawn.
          Set REACT_APP_GOOGLE_MAPS_MAP_ID and rebuild.
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ height, width: "100%" }}
      aria-label="Rider fleet map"
    />
  );
};

export default RiderFleetMap;
